import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  TextField,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  Button,
  Chip,
  Divider,
  Container,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Help as HelpIcon,
  Visibility as VisibilityIcon,
  VideoCall as VideoCallIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { COLORS } from '../data/colors';
import { SIZE_COLORS, SIZES, VINYL_COLORS, ETsy_CONSTANTS, REAL_LISTING_IDS } from '../data/constants';
import './ListingAdmin.css';
import databaseService from '../services/databaseService';
import etsyDataService from '../services/etsyDataService';
import dimensionExtractor from '../services/dimensionExtractor';
import ListingManager from './ListingManager';
import SimpleFileBrowser from './SimpleFileBrowser';

import logger from '../utils/logger.js';
// Create dark theme
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#f48fb1',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0',
    },
  },
});

// Helper function to decode HTML entities in text
const decodeHtmlEntities = (text) => {
  if (!text) return text;
  return text
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ');
};

const ListingAdmin = () => {
  const [sizes, setSizes] = useState([
    { name: 'ATOM', width: '', height: '', price: '', sku: '', packCount: 1, enabled: true },
    { name: 'PIXEL', width: '', height: '', price: '', sku: '', packCount: 1, enabled: false },
    { name: 'NANO', width: '', height: '', price: '', sku: '', packCount: 1, enabled: false },
    { name: 'MICRO', width: '', height: '', price: '', sku: '', packCount: 1, enabled: false },
    { name: 'SMALL', width: '', height: '', price: '', sku: '', packCount: 1, enabled: false },
    { name: 'MEDIUM', width: '', height: '', price: '', sku: '', packCount: 1, enabled: false },
    { name: 'LARGE', width: '', height: '', price: '', sku: '', packCount: 1, enabled: false },
    { name: 'MAX', width: '', height: '', price: '', sku: '', packCount: 1, enabled: false },
  ]);

  const [customColor, setCustomColor] = useState('Multicolor');

  // State for enabled colors (all enabled by default)
  const [enabledColors, setEnabledColors] = useState(
    Object.keys(VINYL_COLORS).reduce((acc, colorName) => {
      acc[colorName] = true;
      return acc;
    }, {})
  );

  // State for listing data
  const [listingData, setListingData] = useState(null);
  const [stickerData, setStickerData] = useState(null);
  const [extractedVariations, setExtractedVariations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [descriptionModal, setDescriptionModal] = useState({ isOpen: false, content: '' });
  const [etsyUpdateModal, setEtsyUpdateModal] = useState({ isOpen: false, data: null, type: '' });
  const [finalDescriptionModal, setFinalDescriptionModal] = useState({ isOpen: false, content: '' });
  const [fileBrowserOpen, setFileBrowserOpen] = useState(false);
  // Track what field the file browser should populate: main svgPath or a set design path
  const [filePickerTarget, setFilePickerTarget] = useState({ type: 'main', index: null });

  // State for description sections
  const [uniqueDescription, setUniqueDescription] = useState('');
  const [includeSizeData, setIncludeSizeData] = useState(true);
  const [boilerPlateText, setBoilerPlateText] = useState(`* The Vehicle Silhouette is NOT INCLUDED, it is already on your windshield. If you need it please see our other listings, we offer a large variety of 'chased' vehicle designs.

If you would like a reversed version or larger for your bumper please contact me and I can make that too!

Made from high-quality fade-resistant vinyl. It can take up to 2 days for the vinyl to fully adhere, please avoid extreme temperatures, car washes, or direct sunlight if possible.

We use the shipping address provided by Etsy for all transactions, please ensure your address is correct and complete. We cannot be responsible for orders sent to incorrect addresses. Please double check your shipping details before finalizing your order.

While we do try and keep stock on hand for immediate shipping, it can take 1-2 business days to prepare your order.

Our stickers are shipped USPS First Class Mail, via Etsy which provides tracking information. While the mail is out of our control, we understand that sometimes things can get lost. Your order is 100% covered by Etsy's guarantee!
**** NOTE: If your address is unconfirmed, we cannot offer replacement stickers. Please make sure your address is accurate and USPS can confirm it. 

Thank you, your satisfaction is our priority!`);

  // Define the standard color order for listings
  const colorOrder = [
    'BLACK', 'WHITE', 'GRAY', 'RED', 'ORANGE', 'YELLOW', 'DOZER_YELLOW', 
    'GREEN', 'FOREST_GREEN', 'ICE_BLUE', 'SKY_BLUE', 'BLUE', 'PURPLE', 
    'VIOLET', 'PINK', 'NUT_BROWN', 'BROWN', 'SILVER_METALLIC', 
    'GOLD_METALLIC', 'COPPER_METALLIC', 'GRAY_HOLOGRAPHIC'
  ];

  // Map color names to display names
  const getDisplayName = (name) => {
    switch (name) {
      case 'BLACK':
        return 'Black - Matte';
      case 'WHITE':
        return 'White - Matte';
      case 'SILVER_METALLIC':
        return 'Silver';
      case 'GOLD_METALLIC':
        return 'Gold';
      case 'COPPER_METALLIC':
        return 'Copper';
      case 'GRAY_HOLOGRAPHIC':
        return 'Gray Holographic';
      default:
        return name.replace(/_/g, ' ');
    }
  };

  // Load listing data on component mount
  useEffect(() => {
    const loadListingData = async () => {
      try {
        setLoading(true);
        
        // Get listing ID from URL parameters, default to T-Rex if none provided
        const urlParams = new URLSearchParams(window.location.search);
                     const listingId = urlParams.get('listingId') || null;
        
        // Loading listing data
        
        // Update page title to show which listing is being edited
        document.title = listingId ? `Listing Editor - ${listingId}` : 'Listing Editor - No Listing Selected';
        
        if (!listingId) {
          logger.log('No listing ID provided, starting with empty state');
          setLoading(false);
          return;
        }
        
        // 🎯 CRITICAL: Get the COMPLETE Etsy data with pricing from the etsy_listing_data table
        const etsyResponse = await fetch(`/.netlify/functions/local-db/etsy-listing-data/${listingId}`);
        if (!etsyResponse.ok) {
          throw new Error(`Failed to fetch Etsy data: ${etsyResponse.status}`);
        }
        
        const etsyData = await etsyResponse.json();
        if (!etsyData.data) {
          throw new Error('No Etsy data found');
        }

        // 🎯 CRITICAL: Parse the JSON fields from the database
        const listingData = etsyData.data.listing_data ? JSON.parse(etsyData.data.listing_data) : {};
        const inventoryData = etsyData.data.inventory_data ? JSON.parse(etsyData.data.inventory_data) : {};
        const imagesData = etsyData.data.images_data ? JSON.parse(etsyData.data.images_data) : {};
        const variationImagesData = etsyData.data.variation_images_data ? JSON.parse(etsyData.data.variation_images_data) : {};
        const extractedVariations = etsyData.data.extracted_variations ? JSON.parse(etsyData.data.extracted_variations) : {};

        // 🔍 DEBUG: Log the parsed inventory data to see if offerings exist
        logger.log('🎭 DEBUG - Parsed inventoryData:', inventoryData);
        logger.log('🎭 DEBUG - First product full data:', inventoryData.products?.[0]);
        logger.log('🎭 DEBUG - First product offerings:', inventoryData.products?.[0]?.offerings);

        // 🎯 CRITICAL: Get the base listing price from the parsed listing data
        let basePrice = '';
        if (listingData.price && listingData.price.amount && listingData.price.divisor) {
          basePrice = (listingData.price.amount / listingData.price.divisor).toString();
          logger.log('🎭 Found base listing price:', basePrice);
        } else {
          logger.log('🎭 No base listing price found in listingData');
        }

        // 🎯 CRITICAL: Extract size data from inventory
        const sizeData = {};
        let hasOfferingsData = false;

        if (inventoryData.products) {
          // Check first product to see if offerings exist
          if (inventoryData.products[0]) {
            hasOfferingsData = !!inventoryData.products[0].offerings;
          }

          // Only log if there's a problem
          if (!hasOfferingsData) {
            logger.warn(`⚠️ WARNING: Inventory data missing offerings array - refetch from Etsy needed for pricing`);
          }

          inventoryData.products.forEach((product, index) => {
            // Extract size from property values
            let sizeName = null;
            if (product.property_values) {
              const sizeProperty = product.property_values.find(pv =>
                pv.property_name === 'Size' ||
                pv.property_name === 'Style' ||
                pv.property_name === 'Variation'
              );

              if (sizeProperty && sizeProperty.values && sizeProperty.values.length > 0) {
                // Extract just the size name (e.g., "MICRO" from "MICRO 1.5w x 2h inches")
                const sizeValue = sizeProperty.values[0];
                const sizeMatch = sizeValue.match(/^(\w+)/);
                let rawSizeName = sizeMatch ? sizeMatch[1].toUpperCase() : sizeValue.toUpperCase();

                // 🎯 CRITICAL: Normalize Etsy size names to UI size names
                const sizeMapping = {
                  'MED': 'MEDIUM',
                  'MEDIUM': 'MEDIUM',
                  'MICRO': 'MICRO',
                  'SMALL': 'SMALL',
                  'LARGE': 'LARGE',
                  'JUMBO': 'JUMBO'
                };

                sizeName = sizeMapping[rawSizeName] || rawSizeName;
              }
            }

            // Extract price from offerings if available
            let price = '';
            if (product.offerings && product.offerings.length > 0) {
              let bestOffering = null;

              for (const offering of product.offerings) {
                if (offering.is_available) {
                  bestOffering = offering;
                  break;
                } else if (!bestOffering) {
                  bestOffering = offering;
                }
              }

              if (bestOffering && bestOffering.price) {
                // Handle both Etsy API formats
                if (bestOffering.price.divisor) {
                  price = (bestOffering.price.amount / bestOffering.price.divisor).toString();
                } else if (typeof bestOffering.price.amount === 'number') {
                  price = bestOffering.price.amount.toString();
                } else if (bestOffering.price.formatted) {
                  price = bestOffering.price.formatted.replace(/[^0-9.]/g, '');
                }
              }
            }

            // Extract SKU and pack count
            const sku = product.sku || '';
            let packCount = 1;
            if (product.name || product.description) {
              const text = `${product.name || ''} ${product.description || ''}`.toUpperCase();
              const packPatterns = [
                /(\d+)\s*STICKERS?/i,
                /PACK\s*OF\s*(\d+)/i,
                /(\d+)\s*PACK/i,
                /QTY\s*(\d+)/i,
                /QUANTITY\s*(\d+)/i
              ];

              for (const pattern of packPatterns) {
                const match = text.match(pattern);
                if (match && match[1]) {
                  const quantity = parseInt(match[1]);
                  if (quantity > 1 && quantity <= 10) {
                    packCount = quantity;
                    break;
                  }
                }
              }
            }

            // Store the size data
            if (sizeName) {
              const existing = sizeData[sizeName] || {};
              sizeData[sizeName] = {
                name: sizeName,
                width: existing.width || '',
                height: existing.height || '',
                price: price || existing.price || '',
                sku: sku || existing.sku || '',
                packCount: packCount,
                enabled: true
              };
            }
          });

          logger.log(`✅ Processed ${inventoryData.products.length} products, found ${Object.keys(sizeData).length} unique sizes`);
        }

        // 🎯 CRITICAL: If no size data found, assign to MICRO by default to ensure pricing is loaded
        if (Object.keys(sizeData).length === 0) {
          logger.log('🎭 No size data found, assigning to MICRO by default');
          logger.log('🎭 Debug - listingData:', listingData);
          logger.log('🎭 Debug - inventoryData:', inventoryData);
          
          // Assign to MICRO by default with the base listing price
          sizeData['MICRO'] = {
            name: 'MICRO',
            width: '',
            height: '',
            price: basePrice,
            sku: '',
            enabled: true
          };
          
          logger.log('🎭 Created MICRO size data:', sizeData['MICRO']);
        }

        // 🎯 CRITICAL: Also get unified data for sticker-specific fields
        const unifiedData = await databaseService.getEtsyListingData(listingId);

        if (unifiedData) {
          const stickerDbData = {
            svgPath: unifiedData.svgPath,
            dimensions: unifiedData.dimensions,
            sizes: unifiedData.sizes,
            category: unifiedData.category
          };
          
          setListingData({
            ...listingData,
            images_data: imagesData.results || imagesData || [],
            variation_images_data: variationImagesData
          });
          
          // Set the full description from the listing data
          if (listingData.description) {
            // Decode HTML entities in the description
            setUniqueDescription(decodeHtmlEntities(listingData.description));
          } else if (listingData.title) {
            // Fallback to title if no description
            setUniqueDescription(decodeHtmlEntities(listingData.title));
          }

          // 🎯 CRITICAL: Set extracted variations from the Etsy data
          setExtractedVariations(extractedVariations);

          // Load real sizes from inventory data and/or extracted dimensions
          // Start with all available sizes (disabled by default)
          const allSizes = SIZES.map(size => ({
            name: size.name,
            width: '',
            height: '',
            price: '',
            sku: '',
            enabled: false
          }));

          // Extract dimensions from description (this works for T-Rex and other stickers)
          const descriptionDimensions = dimensionExtractor.extractDimensionsFromDescription(
            listingData.description || ''
          );

          // Also check if we have dimensions in the stickers table
          let stickerDimensions = {};
          if (unifiedData && unifiedData.dimensions) {
            try {
              stickerDimensions = typeof unifiedData.dimensions === 'string' ? JSON.parse(unifiedData.dimensions) : unifiedData.dimensions;
            } catch (e) {
              logger.warn('Failed to parse sticker dimensions:', e);
            }
          }

          // Check if we have saved sizes data in the unified data
          let savedSizes = [];
          if (unifiedData && unifiedData.sizes) {
            try {
              const parsed = typeof unifiedData.sizes === 'string' ? JSON.parse(unifiedData.sizes) : unifiedData.sizes;
              savedSizes = Array.isArray(parsed) ? parsed : [];
              logger.log('🎭 Loaded saved sizes from database:', savedSizes);
              // Saved sizes data loaded
            } catch (e) {
              logger.warn('Failed to parse saved sizes:', e);
              savedSizes = [];
            }
          }

          // Size name mapping to handle variations
          const sizeNameMapping = {
            'MED': 'MEDIUM',
            'LARGE': 'LARGE',
            'MAX': 'MAX'
          };

          // Update sizes with real data and dimensions
          allSizes.forEach((size, index) => {
            let sizeEnabled = false;
            let sizePrice = '';
            let sizeSku = '';
            let sizePackCount = 1;

            // Check if size exists in inventory data
            if (sizeData[size.name]) {
              sizeEnabled = true;
              sizePrice = sizeData[size.name].price;
              sizeSku = sizeData[size.name].sku;
              sizePackCount = sizeData[size.name].packCount || 1;
            }

            // Check if we have saved data for this size
            const savedSize = Array.isArray(savedSizes) ? savedSizes.find(s => s.name === size.name) : null;
            if (savedSize) {
              // Use saved data to fill in missing fields, but DON'T override inventory data
              // If inventory found the size, keep it enabled; only use saved enabled if inventory didn't find it
              if (!sizeEnabled && savedSize.enabled !== undefined) {
                sizeEnabled = savedSize.enabled;
              }
              if (!sizePrice && savedSize.price) sizePrice = savedSize.price;
              if (!sizeSku && savedSize.sku) sizeSku = savedSize.sku;
              // Preserve packCount from saved data if inventory didn't find one
              if (sizePackCount === 1 && savedSize.packCount !== undefined) {
                sizePackCount = savedSize.packCount;
              }
            }

            // Check if size exists in extracted dimensions (from description)
            let dimensionData = descriptionDimensions[size.name];
            if (!dimensionData) {
              // Try reverse mapping (e.g., MEDIUM -> MED)
              const reverseMapping = Object.entries(sizeNameMapping).find(([key, value]) => value === size.name);
              if (reverseMapping) {
                dimensionData = descriptionDimensions[reverseMapping[0]];
              }
            }

            // If we found dimensions but no inventory data, enable the size
            if (dimensionData && !sizeEnabled) {
              sizeEnabled = true;
            }

            // Check if size exists in sticker dimensions (from database)
            if (!dimensionData && stickerDimensions[size.name]) {
              dimensionData = stickerDimensions[size.name];
              if (!sizeEnabled) {
                sizeEnabled = true;
              }
            }

            // Enable the size if inventory had it, even without price
            if (!sizeEnabled && sizeData[size.name]) {
              sizeEnabled = true;
            }

            // Update the size with all available data
            allSizes[index] = {
              ...size,
              width: dimensionData ? dimensionData.width.toString() : '',
              height: dimensionData ? dimensionData.height.toString() : '',
              price: sizePrice,
              sku: sizeSku,
              packCount: sizePackCount,
              enabled: sizeEnabled
            };
          });

          // Update sizes state with real data
          // Final sizes prepared
          setSizes(allSizes);

          // Load real colors from inventory data and/or enable all colors by default
          const realColors = {};
          
          // Map database color names to our global color constants
          const colorMapping = {
            'Black': 'BLACK',
            'White': 'WHITE', 
            'Gray': 'GRAY',
            'Red': 'RED',
            'Orange': 'ORANGE',
            'Yellow': 'YELLOW',
            'Dozer Yellow': 'DOZER_YELLOW',
            'Green': 'GREEN',
            'Forest Green': 'FOREST_GREEN',
            'Ice Blue': 'ICE_BLUE',
            'Sky Blue': 'SKY_BLUE',
            'Blue': 'BLUE',
            'Purple': 'PURPLE',
            'Violet': 'VIOLET',
            'Pink': 'PINK',
            'Silver': 'SILVER_METALLIC',
            'Gold': 'GOLD_METALLIC',
            'Copper': 'COPPER_METALLIC',
            'Nut Brown': 'NUT_BROWN',
            'Brown': 'BROWN',
            'Gray Holographic': 'GRAY_HOLOGRAPHIC'
          };

          // First, try to extract colors from inventory data
          if (inventoryData.products && inventoryData.products.length > 0) {
            inventoryData.products.forEach(product => {
              const colorProperty = product.property_values?.find(pv => pv.property_name === 'Primary color');
              if (colorProperty && colorProperty.values && colorProperty.values.length > 0) {
                const colorName = colorProperty.values[0];
                const mappedColor = colorMapping[colorName];
                if (mappedColor) {
                  realColors[mappedColor] = true;
                }
              }
            });
          }

          // If we found colors in inventory, use them
          if (Object.keys(realColors).length > 0) {
            setEnabledColors(realColors);
          } else {
            // If no colors found in inventory, enable all colors by default
            // This is the correct behavior for most stickers
            const allColorsEnabled = {};
            colorOrder.forEach(colorName => {
              allColorsEnabled[colorName] = true;
            });
            setEnabledColors(allColorsEnabled);
          }
        }

        if (unifiedData) {
          // Setting sticker data from database
          setStickerData({
            svgPath: unifiedData.svgPath,
            svgPaths: unifiedData.svgPaths || [],
            svgTitles: unifiedData.svgTitles || [],
            packType: unifiedData.packType || null, // Keep for backward compatibility but this will be removed
            setQuantity: unifiedData.setQuantity || null,
            isSet: unifiedData.isSet || false,
            dimensions: unifiedData.dimensions,
            sizes: unifiedData.sizes,
            category: unifiedData.category
          });
          
          // Note: Saved sizes from database are already merged in the inventory processing above (lines 436-485)
          // No need for a second merge here
        } else {
          logger.log('No sticker data found for listing');
        }
              } catch (error) {
          console.error('Error loading listing data:', error);
        } finally {
          setLoading(false);
        }
      };

      loadListingData();
  }, []);

  const handleSizeChange = (index, field, value) => {
    const newSizes = [...sizes];
    newSizes[index][field] = value;
    setSizes(newSizes);
  };

  const handleSizeToggle = (index) => {
    const newSizes = [...sizes];
    newSizes[index].enabled = !newSizes[index].enabled;
    setSizes(newSizes);
  };

  const handleColorToggle = (colorName) => {
    setEnabledColors(prev => ({
      ...prev,
      [colorName]: !prev[colorName]
    }));
  };

  const assignDefaultColors = () => {
    // Enable all colors from the global color list
    const allColorsEnabled = {};
    colorOrder.forEach(colorName => {
      allColorsEnabled[colorName] = true;
    });
    setEnabledColors(allColorsEnabled);
  };

  const createDescription = () => {
    // Get enabled sizes with their details
    const enabledSizes = sizes.filter(size => size.enabled);
    
    // Build the description
    let description = '';
    
    // Add unique description
    if (uniqueDescription.trim()) {
      description += uniqueDescription.trim() + '\n\n';
    }
    
    // Add sizes section (only if toggle is enabled)
    if (includeSizeData && enabledSizes.length > 0) {
      description += '______ TRUEscale Sizes ______\n\n';
      enabledSizes.forEach(size => {
        const sizeInfo = `${size.name}: ${size.width}w x ${size.height}h inches`;
        description += sizeInfo + '\n';
      });
      description += '\n';
    }
    
    // Add boilerplate text
    description += boilerPlateText;
    
    // Open final description modal with save options
    setFinalDescriptionModal({
      isOpen: true,
      content: description
    });
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Container maxWidth={false} sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            Listing Editor {listingData?.title ? `- ${decodeHtmlEntities(listingData.title)}` : listingData ? ' - No Title' : ' - No Listing Selected'}
          </Typography>
          
                      {/* Auto-Search Button */}
            <Button
              variant="contained"
              color="secondary"
              onClick={async () => {
                try {
                  logger.log('Auto-searching for next listing to work on...');
                  
                  // Get current listing ID to exclude it
                  const urlParams = new URLSearchParams(window.location.search);
                  const currentListingId = urlParams.get('listingId');
                  
                  // Use the EXACT SAME working logic as the home page - sortBy: 'sales'
                  const response = await fetch('/.netlify/functions/unified-stickers?hasSvgPath=false&sortBy=sales&sortOrder=desc');
                  if (!response.ok) throw new Error('Failed to fetch listings');
                  
                  const data = await response.json();
                  let listings = data.data || [];
                  
                  // Filter out the current listing
                  if (currentListingId) {
                    listings = listings.filter(listing => listing.etsyListingId !== currentListingId);
                  }
                  
                  if (listings.length === 0) {
                    alert('All listings have svgPath set! 🎉');
                    return;
                  }
                  
                  // The API already sorted by total_sales_volume (bestsellers first)
                  // Just take the first one since it's already sorted correctly
                  const nextListing = listings[0];
                  logger.log('Next listing to work on:', nextListing);
                  logger.log('Available listings:', listings.length);
                  logger.log('Top 5 by sales:', listings.slice(0, 5).map(l => `${l.title} (Sales: ${l.total_sales_volume || 0})`));
                  
                  // Navigate to this listing
                  const newUrl = `/listing-admin?listingId=${nextListing.etsyListingId}`;
                  window.location.href = newUrl;
                  
                } catch (error) {
                  console.error('Error auto-searching:', error);
                  alert('Error finding next listing: ' + error.message);
                }
              }}
              sx={{ minWidth: '150px' }}
            >
              🔍 Find Next Listing
            </Button>
            
            {/* Skip Button */}
            <Button
              variant="outlined"
              color="warning"
              onClick={async () => {
                try {
                  logger.log('Skipping current listing...');
                  
                  // Get current listing ID to exclude it
                  const urlParams = new URLSearchParams(window.location.search);
                  const currentListingId = urlParams.get('listingId');
                  
                  if (!currentListingId) {
                    alert('No current listing to skip');
                    return;
                  }
                  
                  // Use the EXACT SAME working logic as the home page - sortBy: 'sales'
                  const response = await fetch('/.netlify/functions/unified-stickers?hasSvgPath=false&sortBy=sales&sortOrder=desc');
                  if (!response.ok) throw new Error('Failed to fetch listings');
                  
                  const data = await response.json();
                  let listings = data.data || [];
                  
                  // Filter out the current listing
                  listings = listings.filter(listing => listing.etsyListingId !== currentListingId);
                  
                  if (listings.length === 0) {
                    alert('All listings have svgPath set! 🎉');
                    return;
                  }
                  
                  // The API already sorted by total_sales_volume (bestsellers first)
                  const nextListing = listings[0];
                  logger.log('Skipping to next listing:', nextListing);
                  
                  // Navigate to this listing
                  const newUrl = `/listing-admin?listingId=${nextListing.etsyListingId}`;
                  window.location.href = newUrl;
                  
                } catch (error) {
                  console.error('Error skipping listing:', error);
                  alert('Error skipping listing: ' + error.message);
                }
              }}
              sx={{ minWidth: '100px', ml: 1 }}
            >
              ⏭️ Skip
            </Button>
        </Box>

        {/* Listing Manager Section - Moved to top */}
        <Accordion defaultExpanded={false} sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Listing Manager</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ListingManager />
          </AccordionDetails>
        </Accordion>

        {/* Scene Builder File Section - Moved to top */}
        <Accordion defaultExpanded={true} sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Scene Builder File</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
              <TextField
                fullWidth
                placeholder="Path to scene builder file (SVG or PNG)"
                variant="outlined"
                value={stickerData?.svgPath || ''}
                onChange={(e) => {
                  if (stickerData) {
                    logger.log('Updating SVG path to:', e.target.value);
                    setStickerData({
                      ...stickerData,
                      svgPath: e.target.value
                    });
                  } else {
                    logger.log('No stickerData available for update');
                  }
                }}
              />
              <Button
                variant="outlined"
                onClick={() => { setFilePickerTarget({ type: 'main', index: null }); setFileBrowserOpen(true); }}
                sx={{ minWidth: '120px' }}
              >
                Browse Files
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  if (stickerData && stickerData.svgPath) {
                    try {
                      // Get listing ID from URL parameter first, then fallback to listing data, then T-Rex
                      const urlParams = new URLSearchParams(window.location.search);
                      const listingId = urlParams.get('listingId') || listingData?.listing_id || REAL_LISTING_IDS.T_REX;
                      
                      logger.log('Saving SVG path for listing:', listingId);
                      logger.log('SVG path to save:', stickerData.svgPath);
                      logger.log('Full stickerData state:', stickerData);
                      
                      // Prepare data to save - ONLY include specific fields we want to save
                      const dataToSave = {
                        etsyListingId: listingId,
                        svgPath: stickerData.svgPath
                      };
                      
                      // If this is a pack listing, also save pack data
                      if (stickerData?.isSet) {
                        dataToSave.isSet = stickerData.isSet;
                        dataToSave.setQuantity = stickerData.setQuantity;
                        dataToSave.svgPaths = stickerData.svgPaths || [];
                        dataToSave.svgTitles = stickerData.svgTitles || [];
                        // Only include packType if it's not null or 'NULL'
                        if (stickerData.packType && stickerData.packType !== 'NULL') {
                          dataToSave.packType = stickerData.packType;
                        }
                      }
                      
                      logger.log('Data being sent to database:', dataToSave);
                      const result = await databaseService.updateSticker(listingId, dataToSave);
                      if (result) {
                        if (stickerData?.isSet) {
                          alert('Scene builder file path and pack data saved successfully!');
                        } else {
                          alert('Scene builder file path saved successfully!');
                        }
                        logger.log('Save result:', result);
                        
                        // Reload the listing data to show updated values
                        window.location.reload();
                      } else {
                        alert('Failed to save scene builder file path.');
                      }
                    } catch (error) {
                      console.error('Error saving scene builder file path:', error);
                      alert('Error saving scene builder file path: ' + error.message);
                    }
                  } else {
                    alert('Please enter a file path first.');
                  }
                }}
                sx={{ minWidth: '120px' }}
              >
                Save Path
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Select an SVG or PNG file. The path will be saved as a relative path to the public/assets/images/ folder.
              Click "Save Path" to save just the scene builder file path to the database.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Set Management Section - New */}
        <Accordion defaultExpanded={false} sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Set Management</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={stickerData?.isSet || false}
                    onChange={(e) => {
                      if (stickerData) {
                        setStickerData({
                          ...stickerData,
                          isSet: e.target.checked
                        });
                      }
                    }}
                  />
                }
                label="This is a SET listing (multiple designs or multiple copies)"
              />
            </Box>
            
            {stickerData?.isSet && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, color: 'text.secondary' }}>
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Set Quantity"
                    type="number"
                    value={stickerData?.setQuantity || 1}
                    onChange={(e) => {
                      if (stickerData) {
                        const newQuantity = parseInt(e.target.value) || 1;
                        const currentPaths = stickerData.svgPaths || [];
                        const currentTitles = stickerData.svgTitles || [];
                        
                        // For set quantity N, we need N-1 additional paths (since Design 1 uses main svgPath)
                        const additionalPathsNeeded = Math.max(0, newQuantity - 1);
                        
                        // Ensure we have enough paths and titles for additional designs
                        const newPaths = [...currentPaths];
                        const newTitles = [...currentTitles];
                        
                        while (newPaths.length < additionalPathsNeeded) {
                          newPaths.push('');
                          newTitles.push('');
                        }
                        
                        // Trim if reducing quantity
                        if (newPaths.length > additionalPathsNeeded) {
                          newPaths.splice(additionalPathsNeeded);
                          newTitles.splice(additionalPathsNeeded);
                        }
                        
                        setStickerData({
                          ...stickerData,
                          setQuantity: newQuantity,
                          svgPaths: newPaths,
                          svgTitles: newTitles
                        });
                      }
                    }}
                    sx={{ mb: 2 }}
                  />
                </Box>
                
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  SVG Paths for Set Designs
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  <strong>Design 1</strong> uses the main SVG path field above. <strong>Design 2, 3, etc.</strong> use the fields below.
                </Typography>
                
                {/* Show additional design inputs (Design 2, 3, etc.) - Design 1 uses main svgPath */}
                {Array.from({ length: Math.max(0, (stickerData?.setQuantity || 1) - 1) }).map((_, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                    <TextField
                      fullWidth
                      label={`Design ${index + 2} Title`}
                      placeholder={`e.g., Design ${index + 2}`}
                      value={stickerData?.svgTitles?.[index] || ''}
                      onChange={(e) => {
                        if (stickerData) {
                          const newTitles = [...(stickerData.svgTitles || [])];
                          newTitles[index] = e.target.value;
                          setStickerData({
                            ...stickerData,
                            svgTitles: newTitles
                          });
                        }
                      }}
                      sx={{ minWidth: '200px' }}
                    />
                    <TextField
                      fullWidth
                      placeholder={`Path to design ${index + 2} file`}
                      variant="outlined"
                      value={stickerData?.svgPaths?.[index] || ''}
                      onChange={(e) => {
                        if (stickerData) {
                          const newPaths = [...(stickerData.svgPaths || [])];
                          newPaths[index] = e.target.value;
                          setStickerData({
                            ...stickerData,
                            svgPaths: newPaths
                          });
                        }
                      }}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setFilePickerTarget({ type: 'design', index });
                        setFileBrowserOpen(true);
                      }}
                      sx={{ minWidth: '100px' }}
                    >
                      Browse
                    </Button>
                    <IconButton
                      color="error"
                      onClick={() => {
                        if (stickerData) {
                          const newPaths = [...(stickerData.svgPaths || [])];
                          const newTitles = [...(stickerData.svgTitles || [])];
                          newPaths.splice(index, 1);
                          newTitles.splice(index, 1);
                          setStickerData({
                            ...stickerData,
                            svgPaths: newPaths,
                            svgTitles: newTitles,
                            setQuantity: Math.max(1, (stickerData.setQuantity || 1) - 1)
                          });
                        }
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                ))}
                
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={async () => {
                      try {
                        const urlParams = new URLSearchParams(window.location.search);
                        const listingId = urlParams.get('listingId');
                        
                        if (!listingId) {
                          alert('No listing ID found');
                          return;
                        }
                        
                        logger.log('Saving pack data for listing:', listingId);
                        // Normalize paths/titles
                        const rawPaths = Array.isArray(stickerData.svgPaths) ? stickerData.svgPaths : [];
                        const rawTitles = Array.isArray(stickerData.svgTitles) ? stickerData.svgTitles : [];
                        const cleaned = rawPaths
                          .map((p, i) => ({ path: (p || '').trim(), title: (rawTitles[i] || '').trim() }))
                          .filter(x => x.path.length > 0);
                        const cleanedPaths = cleaned.map(x => x.path);
                        const cleanedTitles = cleaned.map(x => x.title);
                        const nextSetQty = Math.max(1, cleanedPaths.length + 1);
                        logger.log('Pack data to save (cleaned):', {
                          isSet: !!stickerData.isSet,
                          setQuantity: nextSetQty,
                          svgPaths: cleanedPaths,
                          svgTitles: cleanedTitles
                        });

                        // Prepare data for pack-only update; do not overwrite main svgPath
                        const updateData = {
                          etsyListingId: String(listingId),
                          isSet: Boolean(stickerData.isSet),
                          setQuantity: nextSetQty,
                          svgPaths: cleanedPaths,
                          svgTitles: cleanedTitles
                        };

                        // Only include packType if it's not null or 'NULL'
                        if (stickerData.packType && stickerData.packType !== 'NULL') {
                          updateData.packType = stickerData.packType;
                        }

                        const result = await databaseService.updateSticker(String(listingId), updateData);
                        
                        if (result) {
                          alert('Pack data saved successfully!');
                          logger.log('Save result:', result);
                          
                          // Reload the listing data to show updated values
                          window.location.reload();
                        } else {
                          alert('Failed to save pack data.');
                        }
                      } catch (error) {
                        console.error('Error saving pack data:', error);
                        alert('Error saving pack data: ' + error.message);
                      }
                    }}
                  >
                    Save Pack Data
                  </Button>
                </Box>
              </>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Description - Sizes Section - Moved to top */}
        <Accordion defaultExpanded={true} sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Typography variant="h6">
                Description - Sizes
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
                  TRUEscale Sizes
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/* Save Sizes Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={async () => {
                  try {
                    // Convert sizes array to dimensions format - MAIN SIZE SECTION
                    const dimensions = {};
                    sizes.forEach(size => {
                      if (size.enabled && size.width && size.height) {
                        // Main design dimensions
                        dimensions[size.name] = {
                          width: parseFloat(size.width),
                          height: parseFloat(size.height),
                          unit: 'inches',
                          source: 'manual'
                        };
                        
                        // Additional design dimensions for SET packs
                        if (stickerData?.isSet && stickerData?.setQuantity > 1) {
                          for (let designIndex = 2; designIndex <= stickerData.setQuantity; designIndex++) {
                            const designKey = `design${designIndex}`;
                            if (size[designKey] && size[designKey].width && size[designKey].height) {
                              dimensions[`${size.name}_design${designIndex}`] = {
                                width: parseFloat(size[designKey].width),
                                height: parseFloat(size[designKey].height),
                                unit: 'inches',
                                source: 'manual',
                                design: designIndex
                              };
                            }
                          }
                        }
                      }
                    });

                    // Save sizes data to the sticker data
                    const updateData = {
                      etsyListingId: String(listingData?.listing_id || REAL_LISTING_IDS.T_REX),
                      sizes: sizes,
                      dimensions: dimensions
                    };

                    // Only include valid stickerData fields, excluding null/NULL packType
                    if (stickerData) {
                      if (stickerData.svgPath) updateData.svgPath = stickerData.svgPath;
                      if (stickerData.svgPaths) updateData.svgPaths = stickerData.svgPaths;
                      if (stickerData.svgTitles) updateData.svgTitles = stickerData.svgTitles;
                      if (stickerData.isSet) updateData.isSet = stickerData.isSet;
                      if (stickerData.setQuantity) updateData.setQuantity = stickerData.setQuantity;
                      if (stickerData.category) updateData.category = stickerData.category;
                      // Only include packType if it's not null or 'NULL'
                      if (stickerData.packType && stickerData.packType !== 'NULL') {
                        updateData.packType = stickerData.packType;
                      }
                    }
                    
                    const result = await databaseService.updateSticker(String(listingData?.listing_id || REAL_LISTING_IDS.T_REX), updateData);
                    
                    if (result) {
                      alert('Sizes and dimensions saved successfully!');
                    } else {
                      alert('Failed to save sizes and dimensions.');
                    }
                  } catch (error) {
                    console.error('Error saving sizes:', error);
                    alert('Error saving sizes.');
                  }
                }}
                sx={{ 
                  fontSize: '0.8rem',
                  px: 2,
                  py: 0.5
                }}
              >
                Save Sizes
              </Button>
            </Box>
            
            {/* Pack Size Information */}
            {stickerData?.isSet && stickerData?.setQuantity > 1 && (
              <Box sx={{ 
                mb: 2, 
                p: 2, 
                bgcolor: 'rgba(0,0,0,0.3)', 
                borderRadius: 1, 
                border: 1, 
                borderColor: 'rgba(255,255,255,0.2)',
                borderLeft: 4,
                borderLeftColor: '#3498db'
              }}>
                <Typography variant="body2" sx={{ 
                  color: '#ffffff',
                  lineHeight: 1.4
                }}>
                  <strong>SET Pack Configuration:</strong> Each enabled size can have unique dimensions for each design. 
                  <br />
                  • <strong>Design 1</strong> (main design): Uses the width/height fields above
                  <br />
                  • <strong>Design 2, 3, etc.</strong>: Use the additional dimension fields below each size
                  <br />
                  • <strong>Price and SKU</strong> remain the same for all designs in a size
                </Typography>
              </Box>
            )}
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr', md: '1fr 1fr' }, 
            gap: 2 
          }}>
            {sizes.map((size, index) => {
              // Find the correct image for this size
              let sizeImage = null;
              
              // First try: Use official variation images mapping if available
              if (listingData?.variation_images_data?.results && listingData.variation_images_data.results.length > 0) {
                const sizeVariation = listingData.variation_images_data.results.find(v => 
                  v.value && v.value.toUpperCase() === size.name.toUpperCase()
                );
                
                if (sizeVariation) {
                  const image = listingData.images_data.find(img => 
                    img.listing_image_id === sizeVariation.image_id
                  );
                  if (image) {
                    sizeImage = image;
                  }
                }
              }
              
              // Second try: Use the listing's actual available_sizes array for proper mapping
              if (!sizeImage && listingData?.images_data && extractedVariations?.sizes) {
                const availableSizes = extractedVariations.sizes;
                const sizeIndex = availableSizes.indexOf(size.name);
                
                if (sizeIndex >= 0) {
                  // Map available sizes to images: first available size = image1, second = image2, etc.
                  // Skip the first image (overall view) and start mapping from second image
                  const imageIndex = sizeIndex + 1;
                  if (imageIndex < listingData.images_data.length) {
                    sizeImage = listingData.images_data[imageIndex];
                  }
                }
              }
              
              // Third try: Fallback mapping based on size order when no variation data exists
              if (!sizeImage && listingData?.images_data && listingData.images_data.length > 1) {
                // For listings without variation mappings, map sizes to images in order
                // Use intelligent size ordering: NANO, MICRO, SMALL, MEDIUM, LARGE, XLARGE
                const sizeOrder = ['NANO', 'MICRO', 'SMALL', 'MEDIUM', 'LARGE', 'XLARGE'];
                const sizeIndex = sizeOrder.indexOf(size.name.toUpperCase());
                
                if (sizeIndex >= 0) {
                  // Skip the first image (overall view) and map sizes to subsequent images
                  const imageIndex = sizeIndex + 1;
                  if (imageIndex < listingData.images_data.length) {
                    sizeImage = listingData.images_data[imageIndex];
                  }
                }
              }
              
              // Final fallback: if no specific mapping, use the first image
              if (!sizeImage && listingData?.images_data?.length > 0) {
                sizeImage = listingData.images_data[0];
              }
              
              return (
                <Box key={size.name} sx={{ 
                  p: 2, 
                  border: 1, 
                  borderColor: size.enabled ? SIZE_COLORS[size.name] : 'rgba(255,255,255,0.2)', 
                  borderRadius: 1,
                  bgcolor: size.enabled ? SIZE_COLORS[size.name] : 'transparent',
                  position: 'relative'
                }}>
                  {/* Header with checkbox and size name */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Checkbox
                      size="small"
                      checked={size.enabled}
                      onChange={() => handleSizeToggle(index)}
                      sx={{
                        color: size.enabled ? '#ffffff' : SIZE_COLORS[size.name],
                        '&.Mui-checked': { 
                          color: size.enabled ? '#ffffff' : SIZE_COLORS[size.name] 
                        }
                      }}
                    />
                    <Typography variant="subtitle1" sx={{ 
                      color: size.enabled ? '#ffffff' : SIZE_COLORS[size.name], 
                      fontWeight: 'bold',
                      flex: 1
                    }}>
                      {size.name}
                    </Typography>
                  </Box>
                  
                  {/* Size thumbnail and form fields side by side */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    {/* Square thumbnail on the left */}
                    <Box sx={{ 
                      width: 60, 
                      height: 60, 
                      overflow: 'hidden',
                      border: 1,
                      borderColor: 'rgba(255,255,255,0.2)',
                      flexShrink: 0
                    }}>
                      {sizeImage ? (
                        <Box
                          component="img"
                          src={sizeImage.url_570xN || sizeImage.url_fullxfull}
                          alt={`${size.name} size`}
                          sx={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <Box sx={{ 
                          width: '100%', 
                          height: '100%', 
                          bgcolor: 'rgba(255,255,255,0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          
                        </Box>
                      )}
                    </Box>
                    
                    {/* Form fields on the right */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flex: 1 }}>
                      <TextField
                        size="small"
                        label="Width"
                        placeholder="Width"
                        variant="outlined"
                        inputProps={{
                          maxLength: 6,
                          pattern: '[0-9.]*',
                          inputMode: 'decimal'
                        }}
                        sx={{ flex: 1, minWidth: '80px' }}
                        disabled={!size.enabled}
                        value={size.width}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          handleSizeChange(index, 'width', value);
                        }}
                      />
                      <TextField
                        size="small"
                        label="Height"
                        placeholder="Height"
                        variant="outlined"
                        inputProps={{
                          maxLength: 6,
                          pattern: '[0-9.]*',
                          inputMode: 'decimal'
                        }}
                        sx={{ flex: '1 1 auto', minWidth: '80px' }}
                        disabled={!size.enabled}
                        value={size.height}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          handleSizeChange(index, 'height', value);
                        }}
                      />
                      <TextField
                        size="small"
                        label="Price"
                        placeholder="Price"
                        variant="outlined"
                        inputProps={{
                          maxLength: 6,
                          pattern: '[0-9.]*',
                          inputMode: 'decimal'
                        }}
                        InputProps={{
                          startAdornment: <Typography variant="body2" sx={{ color: 'text.secondary' }}>$</Typography>,
                        }}
                        sx={{ flex: 1, minWidth: '80px' }}
                        disabled={!size.enabled}
                        value={size.price}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          handleSizeChange(index, 'price', value);
                        }}
                      />
                      <TextField
                        size="small"
                        label="SKU"
                        placeholder="SKU"
                        variant="outlined"
                        sx={{ flex: 1.5, minWidth: '120px' }}
                        disabled={!size.enabled}
                        value={size.sku}
                        onChange={(e) => handleSizeChange(index, 'sku', e.target.value)}
                      />
                      <TextField
                        size="small"
                        label="Pack Count"
                        placeholder="1"
                        variant="outlined"
                        type="number"
                        inputProps={{
                          min: 1,
                          max: 20,
                          step: 1
                        }}
                        sx={{ flex: 1, minWidth: '80px' }}
                        disabled={!size.enabled}
                        value={size.packCount || 1}
                        onChange={(e) => {
                          const value = Math.max(1, parseInt(e.target.value) || 1);
                          handleSizeChange(index, 'packCount', value);
                        }}
                        helperText={size.packCount > 1 ? `${size.packCount} stickers` : 'Single sticker'}
                      />
                    </Box>
                    
                    {/* Additional Design Dimensions for SET Packs */}
                                            {stickerData?.isSet && stickerData?.setQuantity > 1 && size.enabled && (
                      <Box sx={{ 
                        mt: 2, 
                        pt: 2, 
                        borderTop: 1, 
                        borderColor: 'rgba(255,255,255,0.3)',
                        bgcolor: '#1a1a1a',
                        borderRadius: 1,
                        p: 1.5
                      }}>
                        <Typography variant="caption" sx={{ 
                          display: 'block', 
                          mb: 1.5, 
                          color: '#ffffff',
                          fontWeight: 'bold',
                          fontSize: '0.75rem'
                        }}>
                          Additional Design Dimensions
                        </Typography>
                        {Array.from({ length: stickerData.setQuantity - 1 }).map((_, designIndex) => {
                          const designNumber = designIndex + 2; // Design 2, 3, 4, etc.
                          const designKey = `design${designNumber}`;
                          const designTitle = stickerData?.svgTitles?.[designIndex] || `Design ${designNumber}`;
                          
                          // Initialize design dimensions if they don't exist
                          if (!size[designKey]) {
                            size[designKey] = { width: '', height: '' };
                          }
                          
                          return (
                            <Box key={designKey} sx={{ 
                              display: 'flex', 
                              gap: 1, 
                              mb: 1.5, 
                              alignItems: 'center',
                              p: 1,
                              bgcolor: '#2d2d2d',
                              borderRadius: 1,
                              border: 1,
                              borderColor: '#666666'
                            }}>
                              <Typography variant="caption" sx={{ 
                                minWidth: '80px', 
                                color: '#ffffff',
                                fontWeight: 'bold',
                                fontSize: '0.75rem'
                              }}>
                                {designTitle}:
                              </Typography>
                              <TextField
                                size="small"
                                label="Width"
                                placeholder="Width"
                                variant="outlined"
                                inputProps={{
                                  maxLength: 6,
                                  pattern: '[0-9.]*',
                                  inputMode: 'decimal'
                                }}
                                sx={{ 
                                  minWidth: '80px',
                                  '& .MuiInputLabel-root': {
                                    color: '#ffffff'
                                  },
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#1a1a1a',
                                    '& fieldset': {
                                      borderColor: '#666666'
                                    },
                                    '&:hover fieldset': {
                                      borderColor: '#999999'
                                    },
                                    '& input': {
                                      color: '#ffffff'
                                    }
                                  }
                                }}
                                value={size[designKey]?.width || ''}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/[^0-9.]/g, '');
                                  const newSizes = [...sizes];
                                  if (!newSizes[index][designKey]) {
                                    newSizes[index][designKey] = {};
                                  }
                                  newSizes[index][designKey].width = value;
                                  setSizes(newSizes);
                                }}
                              />
                              <TextField
                                size="small"
                                label="Height"
                                placeholder="Height"
                                variant="outlined"
                                inputProps={{
                                  maxLength: 6,
                                  pattern: '[0-9.]*',
                                  inputMode: 'decimal'
                                }}
                                sx={{ 
                                  minWidth: '80px',
                                  '& .MuiInputLabel-root': {
                                    color: '#ffffff'
                                  },
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#1a1a1a',
                                    '& fieldset': {
                                      borderColor: '#666666'
                                    },
                                    '&:hover fieldset': {
                                      borderColor: '#999999'
                                    },
                                    '& input': {
                                      color: '#ffffff'
                                    }
                                  }
                                }}
                                value={size[designKey]?.height || ''}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/[^0-9.]/g, '');
                                  const newSizes = [...sizes];
                                  if (!newSizes[index][designKey]) {
                                    newSizes[index][designKey] = {};
                                  }
                                  newSizes[index][designKey].height = value;
                                  setSizes(newSizes);
                                }}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
          </AccordionDetails>
        </Accordion>


        {/* Sticker Preview Section */}
        {listingData && (
          <Accordion defaultExpanded={true} sx={{ mb: 3 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Sticker Preview</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                {/* Image Preview */}
                <Box sx={{ 
                  width: 200, 
                  height: 200, 
                  border: '2px solid #333', 
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#1a1a1a',
                  overflow: 'hidden'
                }}>
                  {stickerData?.svgPath ? (
                    <img 
                      src={stickerData.svgPath} 
                      alt={listingData.title || 'Sticker preview'}
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '100%', 
                        objectFit: 'contain' 
                      }}
                    />
                  ) : listingData?.images_data && listingData.images_data.length > 0 ? (
                    <img 
                      src={listingData.images_data[0]?.url_fullxfull || listingData.images_data[0]?.url_570xN} 
                      alt={listingData.title || 'Sticker preview'}
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '100%', 
                        objectFit: 'contain' 
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No image available
                    </Typography>
                  )}
                </Box>
                
                {/* Sticker Info */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    {decodeHtmlEntities(listingData.title) || 'No title'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Listing ID:</strong> {listingData.listing_id || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>SVG Path:</strong> {stickerData?.svgPath || 'Not set'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Category:</strong> {stickerData?.category || 'Not set'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Available Sizes:</strong> {
                      (() => {
                        // Show enabled sizes from the sizes array, not dimensions
                        const enabledSizes = sizes.filter(size => size.enabled).map(size => size.name);
                        return enabledSizes.length > 0 ? enabledSizes.join(', ') : 'None configured';
                      })()
                    }
                  </Typography>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Title Section */}
        <Accordion defaultExpanded={false} sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Title</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
                          <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Title text here"
              variant="outlined"
              value={decodeHtmlEntities(listingData?.title) || ''}
              inputProps={{ maxLength: 140 }}
              onChange={(e) => {
                if (listingData) {
                  setListingData({
                    ...listingData,
                    title: e.target.value
                  });
                }
              }}
              helperText={`${(decodeHtmlEntities(listingData?.title) || '').length}/140 characters`}
              FormHelperTextProps={{
                sx: {
                  color: (decodeHtmlEntities(listingData?.title) || '').length > 130 ? 'warning.main' : 'text.secondary',
                  ...((decodeHtmlEntities(listingData?.title) || '').length >= 140 && { color: 'error.main' })
                }
              }}
            />
              <Button
                variant="contained"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  if (listingData && listingData.title) {
                    // Show approval modal for title update
                    setEtsyUpdateModal({
                      isOpen: true,
                      type: 'title',
                      data: {
                        currentTitle: listingData.title,
                        newTitle: listingData.title,
                        listingId: listingData?.listing_id || '846621034'
                      }
                    });
                  } else {
                    alert('Please enter a title first.');
                  }
                }}
                sx={{ minWidth: '120px', alignSelf: 'flex-start' }}
              >
                Update Etsy
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (listingData && listingData.title) {
                    try {
                      const result = await databaseService.upsertEtsyListingData(listingData?.listing_id || '846621034', {
                        ...listingData,
                        title: listingData.title
                      });
                      if (result) {
                        alert('Title saved to local database successfully!');
                      } else {
                        alert('Failed to save title to local database.');
                      }
                    } catch (error) {
                      console.error('Error saving title:', error);
                      alert('Error saving title to local database.');
                    }
                  } else {
                    alert('Please enter a title first.');
                  }
                }}
                sx={{ minWidth: '120px', alignSelf: 'flex-start' }}
              >
                Update Local
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Section Assignment */}
        <Accordion defaultExpanded={false} sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Section Assignment</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
              <TextField
                select
                fullWidth
                label="Category/Section"
                value={stickerData?.category || ''}
                onChange={(e) => {
                  if (stickerData) {
                    setStickerData({
                      ...stickerData,
                      category: e.target.value
                    });
                  }
                }}
                SelectProps={{
                  native: true,
                }}
                helperText="Select the proper section for this listing"
              >
                <option value="">Select Category</option>
                <option value="Animal Stickers">Animal Stickers</option>
                <option value="Cryptids">Cryptids</option>
                <option value="Dinosaur Stickers">Dinosaur Stickers</option>
                <option value="Horror">Horror</option>
                <option value="Offroad Slogans">Offroad Slogans</option>
                <option value="Outdoor Stickers">Outdoor Stickers</option>
                <option value="Police/Military/Fire">Police/Military/Fire</option>
                <option value="Pop Culture Stickers">Pop Culture Stickers</option>
                <option value="Space Stickers">Space Stickers</option>
                <option value="SVGs">SVGs</option>
                <option value="Vehicle Stickers">Vehicle Stickers</option>
                <option value="Vehicles Pop Culture">Vehicles Pop Culture</option>
              </TextField>
              <Button
                variant="contained"
                color="primary"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (stickerData && stickerData.category) {
                    try {
                      // Prepare clean data without null/NULL packType
                      const updateData = {
                        etsyListingId: String(listingData?.listing_id || REAL_LISTING_IDS.T_REX),
                        category: stickerData.category
                      };
                      if (stickerData.svgPath) updateData.svgPath = stickerData.svgPath;
                      if (stickerData.svgPaths) updateData.svgPaths = stickerData.svgPaths;
                      if (stickerData.svgTitles) updateData.svgTitles = stickerData.svgTitles;
                      if (stickerData.isSet) updateData.isSet = stickerData.isSet;
                      if (stickerData.setQuantity) updateData.setQuantity = stickerData.setQuantity;
                      if (stickerData.dimensions) updateData.dimensions = stickerData.dimensions;
                      if (stickerData.sizes) updateData.sizes = stickerData.sizes;
                      if (stickerData.packType && stickerData.packType !== 'NULL') {
                        updateData.packType = stickerData.packType;
                      }

                      const result = await databaseService.updateSticker(String(listingData?.listing_id || REAL_LISTING_IDS.T_REX), updateData);
                      if (result) {
                        alert('Category saved successfully!');
                      } else {
                        alert('Failed to save category.');
                      }
                    } catch (error) {
                      console.error('Error saving category:', error);
                      alert('Error saving category.');
                    }
                  } else {
                    alert('Please select a category first.');
                  }
                }}
                sx={{ minWidth: '120px', alignSelf: 'flex-start' }}
              >
                Save Category
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Current category: <strong>{stickerData?.category || 'Not set'}</strong>
              <br />
              This determines which section the listing appears in on the homepage and affects parallax scene assignments.
            </Typography>
          </AccordionDetails>
        </Accordion>



        {/* Photos and Video Section */}
        <Accordion defaultExpanded={false} sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Photos and Video</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Add up to 10 photos and 1 video.
            </Typography>

          <Grid container spacing={2}>
            {/* Display general product images (first image is overall view) */}
            {listingData?.images_data?.filter((image, index) => {
              // Show only the first image as the general/overall view
              return index === 0;
            }).map((image, index) => (
              <Grid size={{ xs: 6, sm: 4, md: 3 }} key={`image-${index}`}>
                <Card sx={{ height: 120, position: 'relative' }}>
                  <Box
                    component="img"
                    src={image.url_570xN || image.url_fullxfull}
                    alt={`Image ${index + 1}`}
                    sx={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                      borderRadius: 1
                    }}
                  />
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                    }}
                    onClick={() => {
                      // Remove image logic
                      if (listingData) {
                        const newImages = listingData.images_data.filter((_, imgIndex) => imgIndex !== index);
                        setListingData({
                          ...listingData,
                          images_data: newImages
                        });
                      }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  {index === 0 && (
                    <Typography variant="caption" sx={{ position: 'absolute', bottom: 4, left: 4, bgcolor: 'rgba(0,0,0,0.7)', color: 'white', px: 1, borderRadius: 0.5 }}>
                      Primary
                    </Typography>
                  )}
                </Card>
              </Grid>
            ))}

            {/* Empty slots for remaining images (up to 10 total) */}
            {Array.from({ length: Math.max(0, 10 - (listingData?.images_data?.length || 0)) }).map((_, index) => (
              <Grid size={{ xs: 6, sm: 4, md: 3 }} key={`empty-${index}`}>
                <Card sx={{ height: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                  <AddIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="caption" color="text.secondary">Add Image</Typography>
                </Card>
              </Grid>
            ))}

            {/* Empty video slot (separate from image count) */}
            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
              <Card sx={{ height: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <VideoCallIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="caption" color="text.secondary">Add Video</Typography>
              </Card>
            </Grid>
          </Grid>
          </AccordionDetails>
        </Accordion>



        {/* Colors Section */}
        <Accordion defaultExpanded sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              Colors
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', gap: 3 }}>
              {/* Left side - Color Management */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  Color Management
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      assignDefaultColors();
                    }}
                    sx={{ 
                      fontSize: '0.8rem',
                      px: 2,
                      py: 0.5
                    }}
                  >
                    Assign Default
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={async (e) => {
                      e.stopPropagation();
                                          try {
                      // Save enabled colors to the sticker data
                      // Prepare clean data without null/NULL packType
                      const updateData = {
                        etsyListingId: String(listingData?.listing_id || REAL_LISTING_IDS.T_REX),
                        enabledColors: enabledColors
                      };
                      if (stickerData) {
                        if (stickerData.svgPath) updateData.svgPath = stickerData.svgPath;
                        if (stickerData.svgPaths) updateData.svgPaths = stickerData.svgPaths;
                        if (stickerData.svgTitles) updateData.svgTitles = stickerData.svgTitles;
                        if (stickerData.isSet) updateData.isSet = stickerData.isSet;
                        if (stickerData.setQuantity) updateData.setQuantity = stickerData.setQuantity;
                        if (stickerData.dimensions) updateData.dimensions = stickerData.dimensions;
                        if (stickerData.sizes) updateData.sizes = stickerData.sizes;
                        if (stickerData.category) updateData.category = stickerData.category;
                        if (stickerData.packType && stickerData.packType !== 'NULL') {
                          updateData.packType = stickerData.packType;
                        }
                      }

                      const result = await databaseService.updateSticker(String(listingData?.listing_id || REAL_LISTING_IDS.T_REX), updateData);
                        if (result) {
                          alert('Colors saved to local database successfully!');
                        } else {
                          alert('Failed to save colors to local database.');
                        }
                      } catch (error) {
                        console.error('Error saving colors:', error);
                        alert('Error saving colors to local database.');
                      }
                    }}
                    sx={{ 
                      fontSize: '0.8rem',
                      px: 2,
                      py: 0.5
                    }}
                  >
                    Update Local
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={async (e) => {
                      e.stopPropagation();
                      
                      // Get enabled colors
                      const enabledColorNames = Object.keys(enabledColors).filter(color => enabledColors[color]);
                      
                      // Get the real OAuth token
                      const authToken = etsyDataService.getAuthToken();
                      if (!authToken) {
                        alert('❌ No OAuth token found. Please connect to Etsy first.');
                        return;
                      }
                      
                      // Use real constants
                      const shopId = ETsy_CONSTANTS.SHOP_ID;
                      const listingId = listingData?.listing_id || REAL_LISTING_IDS.T_REX;
                      // Discover the real color property ID
                      logger.log('🔍 Discovering color property for listing...');
                      const colorProperty = await databaseService.discoverColorProperty(shopId, listingId, authToken);
                      
                      if (!colorProperty) {
                        alert('❌ No color property found for this listing. This listing may not have color variations configured.');
                        return;
                      }
                      
                      const propertyId = colorProperty.property_id;
                      logger.log('✅ Found color property:', colorProperty.property_name, 'ID:', propertyId);
                      
                      // Convert color names to Etsy format with proper capitalization
                      const etsyColorValues = enabledColorNames.map(color => {
                        const displayName = getDisplayName(color);
                        // Convert to initial caps (e.g., "Black - Matte" -> "Black - Matte")
                        return displayName.split(' ').map(word => 
                          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                        ).join(' ');
                      });
                      // Use real value IDs from the discovered property - only for enabled colors
                      const enabledColorCount = enabledColorNames.length;
                      const etsyColorValueIds = colorProperty.value_ids ? 
                        colorProperty.value_ids.slice(0, enabledColorCount) : 
                        Array.from({length: enabledColorCount}, (_, i) => i + 1);
                      
                                              // Show approval modal with the data that will be sent
                        setEtsyUpdateModal({
                          isOpen: true,
                          type: 'colors',
                          data: {
                            shopId,
                            listingId,
                            propertyId,
                            propertyName: colorProperty.property_name,
                            valueIds: etsyColorValueIds,
                            values: etsyColorValues,
                            enabledColors: enabledColorNames,
                            displayNames: etsyColorValues
                          }
                        });
                    }}
                    sx={{ 
                      fontSize: '0.8rem',
                      px: 2,
                      py: 0.5
                    }}
                  >
                    Update Etsy
                  </Button>
                </Box>
              </Box>

              {/* Center - Available Colors */}
              <Box sx={{ flex: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  Available Colors
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {colorOrder.map((colorName) => {
                    const colorHex = VINYL_COLORS[colorName];
                    const isEnabled = enabledColors[colorName];
                    const textColor = colorHex === '#ffffff' ? '#000000' : '#ffffff';
                    
                    return (
                      <Box
                        key={colorName}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '4px 12px',
                          borderRadius: '16px',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                          backgroundColor: isEnabled ? colorHex : '#333333',
                          color: isEnabled ? textColor : '#888888',
                          border: isEnabled ? `2px solid ${colorHex}` : '2px dashed #666666',
                          '&:hover': {
                            opacity: 0.8
                          }
                        }}
                        onClick={() => handleColorToggle(colorName)}
                      >
                        {getDisplayName(colorName)}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* Right side - Custom Colors */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  Custom Colors
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  <TextField
                    placeholder="Add a color..."
                    variant="outlined"
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <IconButton size="small">
                          <VisibilityIcon />
                        </IconButton>
                      ),
                    }}
                  />
                  <Chip
                    label={customColor}
                    onDelete={() => setCustomColor('')}
                    deleteIcon={<CloseIcon />}
                  />
                  <Button variant="outlined" size="small">
                    Price Change %
                  </Button>
                </Box>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Description - Boiler Plate Section */}
        <Accordion defaultExpanded={false} sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Description Builder</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {/* Size Data Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Include Size Data in Description:
              </Typography>
              <Checkbox
                checked={includeSizeData}
                onChange={(e) => setIncludeSizeData(e.target.checked)}
                color="primary"
              />
              <Typography variant="caption" color="text.secondary">
                {includeSizeData ? 'Size data will be included' : 'Size data will be excluded'}
              </Typography>
            </Box>
            
            {/* Unique Description */}
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 1 }}>
              Unique Description
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Enter unique description for this listing..."
              variant="outlined"
              value={uniqueDescription}
              onChange={(e) => setUniqueDescription(e.target.value)}
              sx={{ mb: 3 }}
              inputProps={{
                style: { resize: 'vertical', minHeight: '120px' }
              }}
            />
            
            {/* Save Description Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={createDescription}
                sx={{ 
                  px: 6, 
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold'
                }}
              >
                Save Description
              </Button>
            </Box>
          
          {/* Boiler Plate - Hidden in Accordion */}
          <Accordion sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.05)', 
                borderRadius: 1,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                Boilerplate Text (Global Template)
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This text is used across all listings. Only modify if you need to update the global template.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={12}
                placeholder="Enter boilerplate description..."
                variant="outlined"
                value={boilerPlateText}
                onChange={(e) => setBoilerPlateText(e.target.value)}
              />
            </AccordionDetails>
          </Accordion>
          </AccordionDetails>
        </Accordion>
      </Container>

      {/* Description Preview Modal */}
      {descriptionModal.isOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            p: 2
          }}
          onClick={() => setDescriptionModal({ isOpen: false, content: '' })}
        >
          <Paper
            sx={{
              maxWidth: '800px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              p: 3,
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Description Preview
              </Typography>
              <IconButton
                onClick={() => setDescriptionModal({ isOpen: false, content: '' })}
                sx={{ color: 'text.secondary' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            
            <TextField
              fullWidth
              multiline
              rows={20}
              variant="outlined"
              value={descriptionModal.content}
              InputProps={{
                readOnly: true,
                sx: { 
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  lineHeight: 1.5
                }
              }}
            />
            
            <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => setDescriptionModal({ isOpen: false, content: '' })}
              >
                Close
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  navigator.clipboard.writeText(descriptionModal.content);
                  // You could add a toast notification here
                }}
              >
                Copy to Clipboard
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Final Description Modal with Save Options */}
      {finalDescriptionModal.isOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            p: 2
          }}
          onClick={() => setFinalDescriptionModal({ isOpen: false, content: '' })}
        >
          <Paper
            sx={{
              maxWidth: '900px',
              width: '100%',
              maxHeight: '85vh',
              overflow: 'auto',
              p: 3,
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                Final Description
              </Typography>
              <IconButton
                onClick={() => setFinalDescriptionModal({ isOpen: false, content: '' })}
                sx={{ color: 'text.secondary' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review and edit the final description before saving:
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={25}
              variant="outlined"
              value={finalDescriptionModal.content}
              onChange={(e) => setFinalDescriptionModal({
                isOpen: true,
                content: e.target.value
              })}
              InputProps={{
                sx: { 
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  lineHeight: 1.5
                }
              }}
            />
            
            <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => setFinalDescriptionModal({ isOpen: false, content: '' })}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={async () => {
                  try {
                    // Save to local database
                    // Prepare clean data without null/NULL packType
                    const updateData = {
                      etsyListingId: String(listingData?.listing_id || REAL_LISTING_IDS.T_REX),
                      description: finalDescriptionModal.content,
                      uniqueDescription: uniqueDescription,
                      boilerPlateText: boilerPlateText
                    };
                    if (stickerData) {
                      if (stickerData.svgPath) updateData.svgPath = stickerData.svgPath;
                      if (stickerData.svgPaths) updateData.svgPaths = stickerData.svgPaths;
                      if (stickerData.svgTitles) updateData.svgTitles = stickerData.svgTitles;
                      if (stickerData.isSet) updateData.isSet = stickerData.isSet;
                      if (stickerData.setQuantity) updateData.setQuantity = stickerData.setQuantity;
                      if (stickerData.dimensions) updateData.dimensions = stickerData.dimensions;
                      if (stickerData.sizes) updateData.sizes = stickerData.sizes;
                      if (stickerData.category) updateData.category = stickerData.category;
                      if (stickerData.packType && stickerData.packType !== 'NULL') {
                        updateData.packType = stickerData.packType;
                      }
                    }

                    const result = await databaseService.updateSticker(String(listingData?.listing_id || REAL_LISTING_IDS.T_REX), updateData);
                    if (result) {
                      alert('✅ Description saved to local database successfully!');
                      setFinalDescriptionModal({ isOpen: false, content: '' });
                    } else {
                      alert('❌ Failed to save description to local database.');
                    }
                  } catch (error) {
                    console.error('Error saving description:', error);
                    alert('❌ Error saving description to local database.');
                  }
                }}
              >
                Save Local
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  try {
                    // Get the real OAuth token
                    const authToken = etsyDataService.getAuthToken();
                    if (!authToken) {
                      alert('❌ No OAuth token found. Please connect to Etsy first.');
                      return;
                    }

                    // Show approval modal for description update
                    setEtsyUpdateModal({
                      isOpen: true,
                      type: 'description',
                      data: {
                        listingId: String(listingData?.listing_id || REAL_LISTING_IDS.T_REX),
                        shopId: '6798191', // Shop ID from database
                        currentDescription: listingData?.description || 'No current description',
                        newDescription: finalDescriptionModal.content
                      }
                    });
                  } catch (error) {
                    console.error('Error preparing Etsy update:', error);
                    alert('❌ Error preparing Etsy update.');
                  }
                }}
              >
                Save to Etsy
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Etsy Update Approval Modal */}
      {etsyUpdateModal.isOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            p: 2
          }}
          onClick={() => setEtsyUpdateModal({ isOpen: false, data: null, type: '' })}
        >
          <Paper
            sx={{
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              p: 3,
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" color="warning.main">
                ⚠️ Etsy Update Approval Required
              </Typography>
              <IconButton
                onClick={() => setEtsyUpdateModal({ isOpen: false, data: null, type: '' })}
                sx={{ color: 'text.secondary' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            
            <Typography variant="body1" sx={{ mb: 3 }}>
              Please review the following changes before updating Etsy:
            </Typography>

            {etsyUpdateModal.type === 'colors' && etsyUpdateModal.data && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Colors to Update on Etsy:
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {etsyUpdateModal.data.displayNames.map((colorName, index) => (
                    <Chip
                      key={index}
                      label={colorName}
                      color="primary"
                      variant="outlined"
                      sx={{ fontSize: '0.8rem' }}
                    />
                  ))}
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <strong>API Details:</strong>
                </Typography>
                
                <Box sx={{ bgcolor: 'rgba(0,0,0,0.1)', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                    <strong>Shop ID:</strong> {etsyUpdateModal.data.shopId}<br/>
                    <strong>Listing ID:</strong> {etsyUpdateModal.data.listingId}<br/>
                    <strong>Property ID:</strong> {etsyUpdateModal.data.propertyId}<br/>
                    <strong>Etsy Values:</strong> {etsyUpdateModal.data.values.join(', ')}<br/>
                    <strong>Value IDs:</strong> {etsyUpdateModal.data.valueIds.join(', ')}
                  </Typography>
                </Box>

                <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                  ⚠️ <strong>Note:</strong> This will replace ALL existing colors on the Etsy listing with the selected colors above.
                </Typography>
              </Box>
            )}

            {etsyUpdateModal.type === 'title' && etsyUpdateModal.data && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Title to Update on Etsy:
                </Typography>
                
                <Box sx={{ bgcolor: 'rgba(0,0,0,0.1)', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}>
                    <strong>New Title:</strong><br/>
                    {etsyUpdateModal.data.newTitle}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <strong>API Details:</strong>
                </Typography>
                
                <Box sx={{ bgcolor: 'rgba(0,0,0,0.1)', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                    <strong>Listing ID:</strong> {etsyUpdateModal.data.listingId}<br/>
                    <strong>Title Length:</strong> {etsyUpdateModal.data.newTitle.length} characters<br/>
                    <strong>API Endpoint:</strong> PUT /v3/application/listings/{etsyUpdateModal.data.listingId}
                  </Typography>
                </Box>

                <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                  ⚠️ <strong>Note:</strong> This will replace the current listing title on Etsy.
                </Typography>
              </Box>
            )}

            {etsyUpdateModal.type === 'description' && etsyUpdateModal.data && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Description to Update on Etsy:
                </Typography>
                
                <Box sx={{ bgcolor: 'rgba(0,0,0,0.1)', p: 2, borderRadius: 1, mb: 2, maxHeight: '200px', overflow: 'auto' }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                    <strong>New Description:</strong><br/>
                    {etsyUpdateModal.data.newDescription}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <strong>API Details:</strong>
                </Typography>
                
                <Box sx={{ bgcolor: 'rgba(0,0,0,0.1)', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                    <strong>Listing ID:</strong> {etsyUpdateModal.data.listingId}<br/>
                    <strong>Description Length:</strong> {etsyUpdateModal.data.newDescription.length} characters<br/>
                    <strong>API Endpoint:</strong> PUT /v3/application/listings/{etsyUpdateModal.data.listingId}
                  </Typography>
                </Box>

                <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                  ⚠️ <strong>Note:</strong> This will replace the current listing description on Etsy.
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => setEtsyUpdateModal({ isOpen: false, data: null, type: '' })}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={async () => {
                  try {
                    if (etsyUpdateModal.type === 'colors' && etsyUpdateModal.data) {
                      // Get the real OAuth token
                      const authToken = etsyDataService.getAuthToken();
                      if (!authToken) {
                        alert('❌ No OAuth token found. Please connect to Etsy first.');
                        return;
                      }
                      
                      // Test the token first
                      logger.log('🔐 Testing OAuth token...');
                      try {
                        const testResponse = await fetch(`/.netlify/functions/etsy-users-me`, {
                          headers: {
                            'Authorization': `Bearer ${authToken}`
                          }
                        });
                        
                        if (!testResponse.ok) {
                          alert('❌ OAuth token is invalid or expired. Please reconnect to Etsy.');
                          return;
                        }
                        
                        logger.log('✅ OAuth token is valid');
                      } catch (error) {
                        console.error('❌ Token test failed:', error);
                        alert('❌ OAuth token test failed. Please reconnect to Etsy.');
                        return;
                      }

                      logger.log('📊 Modal data being sent:', etsyUpdateModal.data);
                      
                      const result = await databaseService.updateEtsyListingProperties(
                        etsyUpdateModal.data.shopId,
                        etsyUpdateModal.data.listingId,
                        etsyUpdateModal.data.propertyId,
                        etsyUpdateModal.data.valueIds,
                        etsyUpdateModal.data.values,
                        authToken
                      );
                      
                      if (result) {
                        alert('✅ Colors updated on Etsy successfully!');
                      } else {
                        alert('❌ Failed to update colors on Etsy.');
                      }
                    } else if (etsyUpdateModal.type === 'title' && etsyUpdateModal.data) {
                      // Get the real OAuth token
                      const authToken = etsyDataService.getAuthToken();
                      if (!authToken) {
                        alert('❌ No OAuth token found. Please connect to Etsy first.');
                        return;
                      }

                      const result = await databaseService.updateEtsyListing(
                        etsyUpdateModal.data.listingId,
                        ETsy_CONSTANTS.SHOP_ID, // Real shop ID
                        { title: etsyUpdateModal.data.newTitle },
                        authToken
                      );
                      
                      if (result) {
                        alert('✅ Title updated on Etsy successfully!');
                      } else {
                        alert('❌ Failed to update title on Etsy.');
                      }
                    } else if (etsyUpdateModal.type === 'description' && etsyUpdateModal.data) {
                      // Get the real OAuth token
                      const authToken = etsyDataService.getAuthToken();
                      if (!authToken) {
                        alert('❌ No OAuth token found. Please connect to Etsy first.');
                        return;
                      }

                      const result = await databaseService.updateEtsyListing(
                        etsyUpdateModal.data.listingId,
                        ETsy_CONSTANTS.SHOP_ID, // Real shop ID
                        { description: etsyUpdateModal.data.newDescription },
                        authToken
                      );
                      
                      if (result) {
                        alert('✅ Description updated on Etsy successfully!');
                        // Close the final description modal as well
                        setFinalDescriptionModal({ isOpen: false, content: '' });
                      } else {
                        alert('❌ Failed to update description on Etsy.');
                      }
                    }
                    
                    setEtsyUpdateModal({ isOpen: false, data: null, type: '' });
                  } catch (error) {
                    console.error('Error updating Etsy:', error);
                    alert('❌ Error updating Etsy: ' + error.message);
                  }
                }}
              >
                Confirm Update to Etsy
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* File Browser */}
      <SimpleFileBrowser
        open={fileBrowserOpen}
        onClose={() => setFileBrowserOpen(false)}
        onFileSelect={(filePath) => {
          logger.log('Selected file path:', filePath);
          if (stickerData) {
            if (filePickerTarget.type === 'design' && filePickerTarget.index != null) {
              const newPaths = [...(stickerData.svgPaths || [])];
              newPaths[filePickerTarget.index] = filePath;
              setStickerData({ ...stickerData, svgPaths: newPaths });
            } else {
              setStickerData({ ...stickerData, svgPath: filePath });
            }
          }
        }}
      />
    </ThemeProvider>
  );
};

// ListingAdmin has no props - it's a standalone admin page component
ListingAdmin.propTypes = {};

export default ListingAdmin; 